import { useState, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { NavTabs } from '@components/NavTabs';
import { useTranslation, useLocations } from '@hooks';
import { RoutePath } from '@constants/routes';
import { childRoutePath } from '@helpers/navigation';
import { FiltersIntegrationType } from '@constants/integrations';
import { GroupByFilter } from '@constants/analytics';
import { subtractYears } from '@helpers/dates';
import { PrintButton } from '@features/Analytics/Common/PrintButton';
import { HeaderWrapper } from '@features/Analytics/styles';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import FiltersPanel from './Reviews/FiltersPanel';
import Reviews from './Reviews';
import OperationalInsights from './OperationalInsights';
import PageContainer from '@components/Layout/PageContainer';

const Container = styled(PageContainer)({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
    '@media print': {
        height: 'auto',
        overflow: 'visible',
    },
});

const StyledNavTabs = styled(NavTabs)({
    '@media print': {
        display: 'none',
    },
});

const AnalyticsReviewsRoute = () => {
    const { pathname } = useLocation();
    const [currentTab, setCurrentTab] = useState(
        pathname === RoutePath.ReviewsAnalyticsKeywords ? RoutePath.ReviewsAnalyticsOperationalInsights : pathname,
    );
    const { t } = useTranslation();

    const [filter, setFilter] = useState({
        locations: [],
        integrations: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
        grouping: GroupByFilter.Months,
    });
    const [filterVersion, setFilterVersion] = useState(0);
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);
    const initDone = useRef(false);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const { mapLocationsToFormValue } = useLocations();

    const availableLocations = useMemo(
        () =>
            locations.filter(
                location =>
                    location?.profiles?.length > 0 && location?.profiles.some(p => p.type in FiltersIntegrationType),
            ),
        [locations],
    );

    useEffect(() => {
        if (availableLocations.length && !initDone.current) {
            const integrations = [
                ...new Set(
                    availableLocations
                        .map(location => location.profiles)
                        .flat()
                        .filter(p => p.type in FiltersIntegrationType)
                        .map(p => p.type),
                ),
            ];
            setFilter(prev => ({ ...prev, integrations, locations: mapLocationsToFormValue(availableLocations) }));
            initDone.current = true;
            setFilterVersion(1);
        }
    }, [availableLocations]);

    const onApplyFilters = () => {
        setFilterVersion(v => v + 1);
    };

    const availableTabs = [
        { label: t('Sidebar.performance'), value: RoutePath.ReviewsAnalyticsReviews },
        { label: t('Sidebar.insights'), value: RoutePath.ReviewsAnalyticsOperationalInsights },
    ];

    const RouteMap = {
        [RoutePath.ReviewsAnalyticsReviews]: 0,
        [RoutePath.ReviewsAnalyticsOperationalInsights]: 1,
    };

    return (
        <Container>
            <AnalyticsWrapper>
                <HeaderWrapper>
                    <FiltersPanel
                        setFiltersOpened={setFiltersOpened}
                        filtersNumber={filterNumber}
                        filterOpened={filterOpened}
                        filter={filter}
                        setFilter={setFilter}
                        type="reviews"
                        setFilterNumber={setFilterNumber}
                        onApplyFilters={onApplyFilters}
                    />
                    <PrintButton />
                </HeaderWrapper>
            </AnalyticsWrapper>
            <StyledNavTabs
                tabLinks={availableTabs}
                onTabClick={selectedTab => setCurrentTab(selectedTab)}
                currentTabIndex={RouteMap[currentTab]}
            />
            <Routes>
                <Route
                    path={childRoutePath(RoutePath.ReviewsAnalyticsReviews, RoutePath.ReviewsAnalytics)}
                    element={<Reviews filter={filter} filterVersion={filterVersion} />}
                />
                <Route
                    path={childRoutePath(RoutePath.ReviewsAnalyticsOperationalInsights, RoutePath.ReviewsAnalytics)}
                    element={<OperationalInsights filter={filter} filterVersion={filterVersion} />}
                />
                <Route
                    path={childRoutePath(RoutePath.ReviewsAnalyticsKeywords, RoutePath.ReviewsAnalytics)}
                    element={<Navigate to={RoutePath.ReviewsAnalyticsOperationalInsights} replace />}
                />
                <Route path="*" element={<Navigate to={RoutePath.ReviewsAnalyticsReviews} replace />} />
            </Routes>
        </Container>
    );
};

export default AnalyticsReviewsRoute;
